import { useMemo } from "react";
import {
  useIsRestoring,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";

import { useAuth } from "~/hooks/useAuth";
import {
  fetchItemAttachments,
  fetchItemReminders,
  fetchItems,
  gearKeys,
  generateId,
  GUEST,
  type Item,
  type ItemAttachment,
  type ItemReminder,
} from "~/state/sync";
import { itemAllowance } from "~/utils/plan-limits";
import { unwrap } from "@brad-tipper/app-frontend";

import { api } from "~/api/client";
import type { ItemPatch } from "~/api/generated/types.gen";

type AttachmentKind = ItemAttachment["kind"];
type ItemCategory = Item["category"];
type ItemStatus = Item["status"];
type ReminderKind = ItemReminder["kind"];

export type ItemInput = {
  name: string;
  kitId?: string | null;
  category?: ItemCategory;
  brand?: string | null;
  model?: string | null;
  serialNumber?: string | null;
  specs?: Record<string, string>;
  purchaseDate?: string | null;
  purchasePrice?: number | null;
  currency?: string;
  vendor?: string | null;
  warrantyExpiresAt?: string | null;
  serviceIntervalMonths?: number | null;
  notes?: string | null;
  status?: ItemStatus;
};

function byName(a: Item, b: Item) {
  return a.name.localeCompare(b.name);
}

function asSpecs(value: unknown): Record<string, string> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const specs: Record<string, string> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry === "string" && entry.trim()) specs[key] = entry;
  }
  return specs;
}

export function itemSpecs(item: Item | null | undefined) {
  return asSpecs(item?.specs);
}

function useUserId() {
  const { user } = useAuth();
  return user?.id ?? null;
}

/**
 * The right rows for the current session: the Postgres rows when signed in,
 * otherwise the guest rows kept on this device (persisted by query-persist.ts).
 */
function useRows<T>(
  keyFor: (scope: string) => readonly [string, string],
  fetcher: () => Promise<T[]>,
) {
  const userId = useUserId();
  const isRestoring = useIsRestoring();
  return useQuery({
    queryKey: keyFor(userId ?? GUEST),
    queryFn: userId ? fetcher : () => [] as T[],
    ...(userId ? {} : { staleTime: Infinity, gcTime: Infinity }),
    enabled: userId ? true : !isRestoring,
  });
}

function useItemsQuery() {
  return useRows<Item>(gearKeys.items, fetchItems);
}

function useAttachmentsQuery() {
  return useRows<ItemAttachment>(gearKeys.attachments, fetchItemAttachments);
}

function useRemindersQuery() {
  return useRows<ItemReminder>(gearKeys.reminders, fetchItemReminders);
}

type Row = { id: string };

/** Writes to the guest copy of a table; there is no server for a guest. */
function writeGuest<T extends Row>(
  queryClient: QueryClient,
  keyFor: (scope: string) => readonly [string, string],
  update: (rows: T[]) => T[],
) {
  queryClient.setQueryData<T[]>(keyFor(GUEST), (rows) => update(rows ?? []));
}

export function useItems(kitId?: string | null) {
  const { data } = useItemsQuery();
  return useMemo(() => {
    const rows = data ?? [];
    return rows
      .filter((item) => (kitId === undefined ? true : item.kit_id === kitId))
      .slice()
      .sort(byName);
  }, [data, kitId]);
}

export function useItem(itemId: string | undefined) {
  const { data } = useItemsQuery();
  return useMemo(
    () =>
      itemId ? ((data ?? []).find((item) => item.id === itemId) ?? null) : null,
    [data, itemId],
  );
}

export function useItemCount() {
  const { data } = useItemsQuery();
  return data?.length ?? 0;
}

export function useItemAttachments(
  itemId: string | undefined,
  kind?: AttachmentKind,
) {
  const { data } = useAttachmentsQuery();
  return useMemo(() => {
    if (!itemId) return [] as ItemAttachment[];
    return (data ?? [])
      .filter(
        (attachment) =>
          attachment.item_id === itemId && (!kind || attachment.kind === kind),
      )
      .slice()
      .sort((a, b) => a.created_at.localeCompare(b.created_at));
  }, [data, itemId, kind]);
}

export function useItemReminders(itemId: string | undefined) {
  const { data } = useRemindersQuery();
  return useMemo(() => {
    if (!itemId) return [];
    return (data ?? [])
      .filter((reminder) => reminder.item_id === itemId)
      .slice()
      .sort((a, b) => a.due_on.localeCompare(b.due_on));
  }, [data, itemId]);
}

export function useUpcomingReminders(limit = 8) {
  const { data: reminders } = useRemindersQuery();
  const { data: items } = useItemsQuery();
  return useMemo(() => {
    if (!reminders) return [];
    const today = new Date().toISOString().slice(0, 10);
    const itemsById = new Map((items ?? []).map((item) => [item.id, item]));
    return reminders
      .slice()
      .sort((a, b) => a.due_on.localeCompare(b.due_on))
      .slice(0, limit)
      .map((reminder) => ({
        ...reminder,
        itemName: itemsById.get(reminder.item_id)?.name ?? "Item",
        overdue: reminder.due_on < today,
      }));
  }, [reminders, items, limit]);
}

export function useItemActions() {
  const queryClient = useQueryClient();
  const userId = useUserId();

  const guestItemCount = () =>
    (queryClient.getQueryData<Item[]>(gearKeys.items(GUEST)) ?? []).length;

  function invalidate() {
    if (!userId) return;
    void queryClient.invalidateQueries({ queryKey: gearKeys.items(userId) });
    void queryClient.invalidateQueries({
      queryKey: gearKeys.attachments(userId),
    });
    void queryClient.invalidateQueries({
      queryKey: gearKeys.reminders(userId),
    });
  }

  const createMutation = useMutation({
    mutationFn: async (input: ItemInput) => {
      const id = generateId();
      if (!userId) {
        const allowance = itemAllowance("free", guestItemCount());
        if (!allowance.allowed) {
          throw new Error(allowance.message ?? "Item limit reached.");
        }
        const now = new Date().toISOString();
        writeGuest<Item>(queryClient, gearKeys.items, (rows) => [
          ...rows,
          {
            id,
            owner_id: GUEST,
            kit_id: input.kitId ?? null,
            name: input.name.trim(),
            category: input.category ?? "other",
            brand: input.brand?.trim() || null,
            model: input.model?.trim() || null,
            serial_number: input.serialNumber?.trim() || null,
            specs: input.specs ?? {},
            purchase_date: input.purchaseDate ?? null,
            purchase_price: input.purchasePrice ?? null,
            currency: input.currency ?? "NZD",
            vendor: input.vendor?.trim() || null,
            warranty_expires_at: input.warrantyExpiresAt ?? null,
            service_interval_months: input.serviceIntervalMonths ?? null,
            notes: input.notes?.trim() || null,
            status: input.status ?? "in_use",
            deleted: false,
            created_at: now,
            updated_at: now,
          },
        ]);
        return id;
      }
      await unwrap(
        api.putItem({
          path: { id },
          body: {
            kit_id: input.kitId ?? null,
            name: input.name.trim(),
            category: input.category ?? "other",
            brand: input.brand?.trim() || null,
            model: input.model?.trim() || null,
            serial_number: input.serialNumber?.trim() || null,
            specs: input.specs ?? {},
            purchase_date: input.purchaseDate ?? null,
            purchase_price: input.purchasePrice ?? null,
            currency: input.currency ?? "NZD",
            vendor: input.vendor?.trim() || null,
            warranty_expires_at: input.warrantyExpiresAt ?? null,
            service_interval_months: input.serviceIntervalMonths ?? null,
            notes: input.notes?.trim() || null,
            status: input.status ?? "in_use",
          },
        }),
      );
      return id;
    },
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      itemId,
      input,
    }: {
      itemId: string;
      input: Partial<ItemInput>;
    }) => {
      const patch: ItemPatch = {};
      if (input.name !== undefined) patch.name = input.name.trim();
      if (input.kitId !== undefined) patch.kit_id = input.kitId;
      if (input.category !== undefined) patch.category = input.category;
      if (input.brand !== undefined) patch.brand = input.brand?.trim() || null;
      if (input.model !== undefined) patch.model = input.model?.trim() || null;
      if (input.serialNumber !== undefined) {
        patch.serial_number = input.serialNumber?.trim() || null;
      }
      if (input.specs !== undefined) patch.specs = input.specs;
      if (input.purchaseDate !== undefined) {
        patch.purchase_date = input.purchaseDate;
      }
      if (input.purchasePrice !== undefined) {
        patch.purchase_price = input.purchasePrice;
      }
      if (input.currency !== undefined) patch.currency = input.currency;
      if (input.vendor !== undefined) {
        patch.vendor = input.vendor?.trim() || null;
      }
      if (input.warrantyExpiresAt !== undefined) {
        patch.warranty_expires_at = input.warrantyExpiresAt;
      }
      if (input.serviceIntervalMonths !== undefined) {
        patch.service_interval_months = input.serviceIntervalMonths;
      }
      if (input.notes !== undefined) patch.notes = input.notes?.trim() || null;
      if (input.status !== undefined) patch.status = input.status;
      if (Object.keys(patch).length === 0) return;
      if (!userId) {
        writeGuest<Item>(queryClient, gearKeys.items, (rows) =>
          rows.map((row) =>
            row.id === itemId
              ? ({
                  ...row,
                  ...patch,
                  updated_at: new Date().toISOString(),
                } as Item)
              : row,
          ),
        );
        return;
      }
      await unwrap(api.patchItem({ path: { id: itemId }, body: patch }));
    },
    onSuccess: invalidate,
  });

  const addAttachmentMutation = useMutation({
    mutationFn: async ({
      itemId,
      path,
      kind,
      filename,
    }: {
      itemId: string;
      path: string;
      kind: AttachmentKind;
      filename?: string;
    }) => {
      if (!userId) {
        throw new Error("Create a free account to attach files.");
      }
      const id = generateId();
      await unwrap(
        api.putItemAttachment({
          path: { id },
          body: {
            item_id: itemId,
            kind,
            path,
            filename: filename?.trim() || null,
          },
        }),
      );
      return id;
    },
    onSuccess: invalidate,
  });

  const removeAttachmentMutation = useMutation({
    mutationFn: async (attachmentId: string) => {
      if (!userId) {
        writeGuest<ItemAttachment>(queryClient, gearKeys.attachments, (rows) =>
          rows.filter((row) => row.id !== attachmentId),
        );
        return;
      }
      // The API deletes the stored file along with the attachment.
      await unwrap(api.deleteItemAttachment({ path: { id: attachmentId } }));
    },
    onSuccess: invalidate,
  });

  const addReminderMutation = useMutation({
    mutationFn: async ({
      itemId,
      kind,
      dueOn,
      notes,
    }: {
      itemId: string;
      kind: ReminderKind;
      dueOn: string;
      notes?: string;
    }) => {
      const id = generateId();
      if (!userId) {
        const now = new Date().toISOString();
        writeGuest<ItemReminder>(queryClient, gearKeys.reminders, (rows) => [
          ...rows,
          {
            id,
            owner_id: GUEST,
            item_id: itemId,
            kind,
            due_on: dueOn,
            notes: notes?.trim() || null,
            deleted: false,
            created_at: now,
            updated_at: now,
          },
        ]);
        return id;
      }
      await unwrap(
        api.putItemReminder({
          path: { id },
          body: {
            item_id: itemId,
            kind,
            due_on: dueOn,
            notes: notes?.trim() || null,
          },
        }),
      );
      return id;
    },
    onSuccess: invalidate,
  });

  const removeReminderMutation = useMutation({
    mutationFn: async (reminderId: string) => {
      if (!userId) {
        writeGuest<ItemReminder>(queryClient, gearKeys.reminders, (rows) =>
          rows.filter((row) => row.id !== reminderId),
        );
        return;
      }
      await unwrap(api.deleteItemReminder({ path: { id: reminderId } }));
    },
    onSuccess: invalidate,
  });

  const removeMutation = useMutation({
    mutationFn: async (itemId: string) => {
      if (!userId) {
        writeGuest<Item>(queryClient, gearKeys.items, (rows) =>
          rows.filter((row) => row.id !== itemId),
        );
        writeGuest<ItemReminder>(queryClient, gearKeys.reminders, (rows) =>
          rows.filter((row) => row.item_id !== itemId),
        );
        return;
      }
      // The API trashes the item's attachments (and their files) and reminders too.
      await unwrap(api.deleteItem({ path: { id: itemId } }));
    },
    onSuccess: invalidate,
  });

  return {
    /** Resolves to the new item id. Awaited by the create screens so DB-side
     * plan-limit errors reach their catch. */
    create: (input: ItemInput) => createMutation.mutateAsync(input),
    update: (itemId: string, input: Partial<ItemInput>): void =>
      updateMutation.mutate({ itemId, input }),
    addAttachment: (
      itemId: string,
      path: string,
      kind: AttachmentKind,
      filename?: string,
    ): void => addAttachmentMutation.mutate({ itemId, path, kind, filename }),
    removeAttachment: (attachmentId: string): void =>
      removeAttachmentMutation.mutate(attachmentId),
    addReminder: (
      itemId: string,
      kind: ReminderKind,
      dueOn: string,
      notes?: string,
    ): void => addReminderMutation.mutate({ itemId, kind, dueOn, notes }),
    removeReminder: (reminderId: string): void =>
      removeReminderMutation.mutate(reminderId),
    remove: (itemId: string): void => removeMutation.mutate(itemId),
  };
}
